import React, {useState} from 'react';
import {Collapse} from 'reactstrap';
import {getCurrencySymbol, toCurrency} from 'utils/helpers';

// The API only returns per-type totals, so each tax's share is split by its rate
const getTaxBreakdown = (taxes = [], totals = {}) => {
    const rateSum = type =>
        taxes
            .filter(tax => tax?.type === type)
            .reduce((acc, tax) => acc + Number(tax?.rate || 0), 0);
    const rateSums = {included: rateSum('included'), excluded: rateSum('excluded')};

    return taxes.map(tax => {
        const total = Number(totals[tax?.type] || 0);
        const sum = rateSums[tax?.type];
        return {...tax, value: sum ? (total * Number(tax?.rate || 0)) / sum : 0};
    });
};

const InvoicePrice = ({invoice, currency}) => {
    const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);

    const {amount, other_info, terms_and_condition} = invoice || {};
    const {
        taxes_value,
        total_included_tax,
        total_excluded_tax,
        total_item_taxes,
        taxes,
        coupon_value,
        calculated_items,
    } = other_info || {};

    const subTotal = calculated_items?.reduce((acc, item) => acc + (item?.total || 0), 0);
    const currencyCode = getCurrencySymbol(currency);

    // Included taxes are already part of the item prices, so only excluded (and item) taxes add to the total
    const hasTaxBreakdown = total_included_tax !== undefined || total_excluded_tax !== undefined;
    const taxValue = hasTaxBreakdown
        ? Number(total_excluded_tax || 0) + Number(total_item_taxes || 0)
        : taxes_value;

    const taxBreakdown = getTaxBreakdown(taxes, {
        included: total_included_tax,
        excluded: total_excluded_tax,
    });
    const hasMultipleTaxes = taxBreakdown.length + (Number(total_item_taxes) > 0 ? 1 : 0) > 1;

    // API doesn't always return coupon_value, so derive the discount from the totals:
    // amount = subTotal - coupon + excluded tax + item taxes (included tax is already in subTotal)
    const derivedCoupon = hasTaxBreakdown
        ? subTotal + Number(taxValue || 0) - Number(amount || 0)
        : 0;
    const couponValue = Number(coupon_value) || (derivedCoupon >= 0.01 ? derivedCoupon : 0);

    return (
        <div className="flex__between flex-wrap invoice__description row">
            <div className="col-sm-6">
                {terms_and_condition && <p className="fw-bold mb-1"> Terms & Conditions</p>}
                <span>{terms_and_condition}</span>
            </div>
            <div className="col-sm-5 p-3 bg-light">
                <div className="flex__between">
                    <span>Sub Total</span>
                    <p>{`${currencyCode} ${toCurrency(subTotal)}`}</p>
                </div>

                {couponValue > 0 && (
                    <div className="flex__between">
                        <span>Coupon</span>
                        <p>{`${currencyCode} ${toCurrency(couponValue)}`}</p>
                    </div>
                )}

                <div className="flex__between">
                    {hasMultipleTaxes ? (
                        <span role="button" onClick={() => setShowTaxBreakdown(prev => !prev)}>
                            Tax {showTaxBreakdown ? '▴' : '▾'}
                        </span>
                    ) : (
                        <span>Tax</span>
                    )}
                    <p>{`${currencyCode} ${toCurrency(taxValue || 0)}`}</p>
                </div>

                {hasMultipleTaxes && (
                    <Collapse isOpen={showTaxBreakdown}>
                        <div className="ps-3 mb-3 small">
                            {taxBreakdown.map((tax, index) => (
                                <div className="flex__between" key={`${tax?.name}-${index}`}>
                                    <span>
                                        {tax?.name?.trim()} ({tax?.rate}%
                                        {tax?.type === 'included' ? ', included in price' : ''})
                                    </span>
                                    <span>{`${currencyCode} ${toCurrency(tax.value)}`}</span>
                                </div>
                            ))}
                            {Number(total_item_taxes) > 0 && (
                                <div className="flex__between">
                                    <span>Item taxes</span>
                                    <span>{`${currencyCode} ${toCurrency(total_item_taxes)}`}</span>
                                </div>
                            )}
                        </div>
                    </Collapse>
                )}

                <div className="flex__between border-top pt-2 fw-bold">
                    <span>Total</span>
                    <p className="mb-0">{`${currencyCode} ${toCurrency(amount)}`}</p>
                </div>
            </div>
        </div>
    );
};

export default InvoicePrice;
